import { AxiosError } from 'axios';
import { useQuery } from 'react-query';
import { Axios } from '@utils/api/axios-instance';
import { GET_URL_NEW } from '@utils/api/endpoints';
import { BaseError } from '@utils/types/shared/base';
import { useNewAppSearchParams } from '@utils/hooks/use-new-app-search-params';
import { useSearchParams } from 'react-router-dom';
import { SupportedCurrency } from '@utils/types/organization/organization';

export type Billables = {
  result: Billable[];
  count: number;
  total_results: number;
};

export type Billable = {
  id: number;
  purchaser: string;
  organization: string;
  billable_cost: number;
  currency: SupportedCurrency;
  period_start: string; // ISO yyyy-mm-dd
  period_end: string; // ISO yyyy-mm-dd
  subscription_id: number;
  usage_stt: number;
  usage_alignment: number;
  usage_translation: number;
  current_cost: string;
  custom_models_trained: number;
  custom_models_hosted: number;
  stt_templates_exported: number;
  billing_cycle: {
    id: number;
    cycle_start: string;
    cycle_end: string;
    status: string;
    is_locked: boolean;
    locked_at: string;
  };
};

export const useGetBillablesBo = (periodEnd?: string) => {
  const { searchParams } = useNewAppSearchParams();
  const [sp] = useSearchParams();

  const offset = Number.isFinite(Number(searchParams.offset))
    ? Number(searchParams.offset)
    : 0;
  const limit = Number.isFinite(Number(searchParams.limit))
    ? Number(searchParams.limit)
    : 25;

  const sortField =
    (searchParams.sort as string | undefined) || 'billable_cost';
  const sortDirection =
    (searchParams.direction as 'asc' | 'desc' | undefined) || 'desc';

  const allQ = sp.getAll('q').filter(Boolean);

  const fetchBillables = async () => {
    const { billablesWith } = GET_URL_NEW({
      offset,
      limit,
      q: allQ.length > 0 ? allQ : undefined,
      sort: sortField,
      direction: sortDirection
    });

    // pass period_end as an extra param to the builder
    const url = billablesWith(periodEnd ? { period_end: periodEnd } : {});

    const { data } = await Axios.get<Billables>(url);
    return data;
  };

  return useQuery<Billables, AxiosError<BaseError>>(
    [
      'billablesBo',
      offset,
      limit,
      allQ.join(','),
      sortField,
      sortDirection,
      periodEnd
    ],
    fetchBillables,
    { useErrorBoundary: false, keepPreviousData: true }
  );
};
